/**
 * 検査ポリシー `/dlp/policies`。一覧(行を押すと編集)+「ポリシーを追加」。編集・削除は dlp:admin、表示は viewer。
 * 既定は常に 1 行(サーバーが保証)。
 */
import { Alert, Button, Tag } from "antd";
import { useState } from "react";

import type { DlpPolicy } from "../../lib/api";
import { DataTable, PageHeader, TimeAgo, type DataColumn } from "../../ui";
import { errorText, useDlpPerms, useDlpPolicies } from "./hooks";
import { PolicyForm } from "./PolicyForm";

export function PoliciesPage() {
  const { canAdmin } = useDlpPerms();
  const query = useDlpPolicies();
  // null = 閉じている / "new" = 追加 / DlpPolicy = 編集(または表示)
  const [editing, setEditing] = useState<DlpPolicy | "new" | null>(null);
  const rows = query.data?.items ?? [];

  const columns: DataColumn<DlpPolicy>[] = [
    {
      title: "名前",
      ellipsis: true,
      onCell: (row) => ({ title: [row.name, row.description].filter(Boolean).join("\n") }),
      width: 320,
      render: (_: unknown, p) => (
        <div>
          <span>
            {p.name} {p.is_default && <Tag color="blue">既定</Tag>}
          </span>
          {p.description && <span className="ui-cell-sub">{p.description}</span>}
        </div>
      ),
    },
    {
      title: "探す語",
      ellipsis: false,
      width: 320,
      render: (_: unknown, p) => p.patterns.map((x) => x.name).join(" / "),
    },
    { title: "場所", ellipsis: true, onCell: (row) => ({ title: row.roots.join(" / ") }), width: 320, render: (_: unknown, p) => p.roots.join(" / ") },
    { title: "拡張子", ellipsis: false, width: 240, render: (_: unknown, p) => p.extensions.join(" ") },
    {
      title: "上限",
      ellipsis: false,
      width: 200,
      render: (_: unknown, p) => (
        <span className="ui-num">
          {p.max_file_mb} MB / {p.max_files.toLocaleString()} 件 / {p.time_budget_min} 分
        </span>
      ),
    },
    { title: "更新", ellipsis: false, dataIndex: "updated_at", width: 160, render: (v: string) => <TimeAgo iso={v} /> },
  ];

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="検査ポリシー"
          description="退職時スキャンで何を・どこで・どの拡張子から探すか。依頼時に渡した内容はスキャンに写すので、後から変えても過去の結果の意味は変わらない。PDF は読まない(v0)。"
          primary={
            canAdmin ? (
              <Button type="primary" onClick={() => setEditing("new")}>
                ポリシーを追加
              </Button>
            ) : undefined
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="ポリシーを取得できなかった" description={errorText(query.error)} />
        )}
        <DataTable<DlpPolicy>
          columns={columns}
          scrollX={columns.reduce((sum, c) => sum + c.width, 0)}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={query.data?.total ?? rows.length}
          onPage={() => undefined}
          onRowClick={(row) => setEditing(row)}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: "ポリシーがない(既定の 1 行はマイグレーションでシードされる)",
            action: canAdmin ? (
              <Button type="primary" onClick={() => setEditing("new")}>
                ポリシーを追加
              </Button>
            ) : undefined,
          }}
        />
      </div>
      <PolicyForm
        open={editing !== null}
        policy={editing === "new" ? null : editing}
        readOnly={!canAdmin}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}
